import { useEffect, useRef } from 'react';
import type { AutomationLane, AutomationTarget, Track, TrackKind } from '@/domain/types';
import {
  MAX_TRACK_HEIGHT,
  MAX_TRACK_HEIGHT_SCALE,
  MIN_TRACK_HEIGHT,
  MIN_TRACK_HEIGHT_SCALE,
} from '@/domain/project';
import { gainToDb } from '@/util/assert';
import { command } from '@/commands/registry';
import * as TrackCommands from '@/commands/trackCommands';
import * as AutomationCommands from '@/commands/automationCommands';
import { describeTarget } from '@/engine/automation';
import { describeFreeze, isFreezeStale } from '@/domain/freeze';
import { TRACK_PARAM_DESCRIPTORS, isAutomatable, effectDescriptor } from '@/engine/effects/descriptors';
import { getServices } from '@/state/services';
import { useDaw } from '@/state/store';
import { clipEndTicks } from '@/domain/selectors';
import { clipsOnLane } from '@/domain/slice';
import * as TakeCommands from '@/commands/takeCommands';
import { laneLayout, takeLaneCount, trackHeightScale } from './timeline/view';
import { MAX_TAKE_NAME, trackTakeName } from '@/domain/takes';
import { Meter } from './common/Meter';

const KIND_LABEL: Record<TrackKind, string> = {
  audio: 'Audio',
  instrument: 'Instrument',
  bus: 'Bus',
  aux: 'Return',
  master: 'Master',
};

export function TrackHeaders(): JSX.Element {
  const project = useDaw((s) => s.project);
  const selection = useDaw((s) => s.selection);
  const listRef = useRef<HTMLDivElement>(null);

  // The header column scrolls with the lanes: they are one surface split by a
  // divider, and letting them drift apart would be disorienting.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = project.view.scrollTop;
  }, [project.view.scrollTop]);

  const { lanes } = laneLayout(project);

  return (
    <div className="track-headers">
      <div className="track-headers__ruler">
        <button className="btn btn--ghost" onClick={() => useDaw.getState().addTrack('audio')} title="Add an audio track">
          + Audio
        </button>
        <button
          className="btn btn--ghost"
          onClick={() => useDaw.getState().addTrack('instrument')}
          title="Add an instrument track, with a synth loaded and ready to play"
        >
          + Inst
        </button>
        <button className="btn btn--ghost" onClick={() => useDaw.getState().addTrack('bus')} title="Add a bus">
          + Bus
        </button>
        <TrackHeightSlider />
      </div>
      <div className="track-headers__list" ref={listRef}>
        <div style={{ transform: `translateY(${-project.view.scrollTop}px)` }}>
          {lanes.map((lane) => {
            const track = project.tracks[lane.trackId];
            if (!track) return null;
            if (lane.takeLane) {
              return (
                <TakeLaneHeader
                  key={`${lane.trackId}:take${lane.takeLane}`}
                  track={track}
                  takeLane={lane.takeLane}
                  height={lane.height}
                />
              );
            }
            if (lane.laneId) {
              const automation = project.automation[lane.laneId];
              return automation ? (
                <AutomationLaneHeader
                  key={lane.laneId}
                  lane={automation}
                  track={track}
                  height={lane.height}
                  selected={selection.laneId === lane.laneId}
                />
              ) : null;
            }
            return (
              <TrackHeader
                key={track.id}
                track={track}
                height={lane.height}
                selected={selection.trackId === track.id}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TrackHeader({ track, height, selected }: { track: Track; height: number; selected: boolean }): JSX.Element {
  const project = useDaw((s) => s.project);
  const dispatch = useDaw((s) => s.dispatch);
  const inputDevices = useDaw((s) => s.inputDevices);
  const resizeRef = useRef<{ startY: number; startHeight: number } | null>(null);

  const onResizePointerDown = (event: React.PointerEvent): void => {
    event.currentTarget.setPointerCapture(event.pointerId);
    resizeRef.current = { startY: event.clientY, startHeight: track.height };
  };

  const onResizePointerMove = (event: React.PointerEvent): void => {
    const state = resizeRef.current;
    if (!state) return;
    // The pointer moves in screen pixels but track.height is stored unscaled,
    // so undo the vertical zoom or the edge would lag or outrun the cursor.
    const delta = (event.clientY - state.startY) / trackHeightScale(project);
    const next = Math.min(MAX_TRACK_HEIGHT, Math.max(MIN_TRACK_HEIGHT, state.startHeight + delta));
    dispatch(command(TrackCommands.setTrackHeight, { trackId: track.id, height: Math.round(next) }));
  };

  const onResizePointerUp = (event: React.PointerEvent): void => {
    resizeRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
    getServices().commands.breakCoalescing();
  };

  const volumeTarget: AutomationTarget = {
    kind: 'track',
    trackId: track.id,
    effectId: null,
    paramId: 'volume',
  };
  const panTarget: AutomationTarget = { kind: 'track', trackId: track.id, effectId: null, paramId: 'pan' };

  const freezing = useDaw((s) => s.freezingTrackId === track.id);
  const stale = isFreezeStale(project, track.id);
  const compact = height < 68;

  return (
    <div
      className={`track-head ${selected ? 'track-head--selected' : ''}`}
      style={{ height }}
      onPointerDown={() => useDaw.getState().setSelection({ trackId: track.id })}
    >
      <div className="track-head__accent" style={{ background: track.color }} />
      {track.freeze && <div className="track-head__frozen" aria-hidden="true" />}

      <div className="track-head__row">
        <input
          className="track-head__name"
          value={track.name}
          aria-label={`Track name for ${track.name}`}
          onChange={(e) => dispatch(command(TrackCommands.renameTrack, { trackId: track.id, name: e.target.value || 'Track' }))}
        />
        <button
          className={`chip chip--mute ${track.mute ? 'chip--on' : ''}`}
          onClick={() => dispatch(command(TrackCommands.setTrackMute, { trackId: track.id, mute: !track.mute }))}
          title="Mute"
          aria-pressed={track.mute}
        >
          M
        </button>
        <button
          className={`chip chip--solo ${track.solo ? 'chip--on' : ''}`}
          onClick={(e) =>
            dispatch(
              command(TrackCommands.setTrackSolo, {
                trackId: track.id,
                solo: !track.solo,
                exclusive: !e.altKey,
              }),
            )
          }
          title="Solo (hold Alt to add to the solo group)"
          aria-pressed={track.solo}
        >
          S
        </button>
        {track.kind !== 'master' && (
          <button
            className={`chip ${track.freeze ? (stale ? 'chip--arm chip--on' : 'chip--mon chip--on') : ''}`}
            disabled={freezing}
            onClick={() => {
              if (track.freeze) useDaw.getState().unfreezeTrack(track.id);
              else void useDaw.getState().freezeTrack(track.id);
            }}
            title={
              freezing
                ? 'Rendering…'
                : track.freeze
                  ? stale
                    ? 'This track has been edited since it was frozen. Unfreeze, or freeze again to hear the change.'
                    : describeFreeze(track.freeze, false)
                  : 'Render this track down to audio so its instrument and effects stop running'
            }
            aria-label={`Freeze ${track.name}`}
            aria-pressed={Boolean(track.freeze)}
          >
            {freezing ? '…' : stale ? 'F!' : 'F'}
          </button>
        )}
        {track.freeze && stale && !freezing && (
          <button
            className="chip chip--arm"
            aria-label={`Refresh the freeze on ${track.name}`}
            title="Render this track again so the freeze matches what is on it now."
            onClick={() => void useDaw.getState().freezeTrack(track.id)}
          >
            ↻
          </button>
        )}
        {takeLaneCount(project, track.id) > 0 && (
          <button
            className={`chip ${track.showTakeLanes ? 'chip--mon chip--on' : ''}`}
            onClick={() =>
              dispatch(command(TakeCommands.showTakeLanes, { trackId: track.id, show: !track.showTakeLanes }))
            }
            title={`${takeLaneCount(project, track.id)} take(s) — show the lanes to comp between them`}
            aria-label={`Take lanes for ${track.name}`}
            aria-pressed={track.showTakeLanes}
          >
            T{takeLaneCount(project, track.id)}
          </button>
        )}
        <button
          className={`chip ${track.showAutomation ? 'chip--mon chip--on' : ''}`}
          onClick={() => {
            if (track.automationLaneIds.length === 0) {
              // Nothing to show yet: the first click creates the lane a user
              // almost always wants, rather than revealing an empty section.
              dispatch({
                type: AutomationCommands.addAutomationLane.type,
                params: { trackId: track.id, kind: 'track', paramId: 'volume' },
              });
              return;
            }
            dispatch(
              command(AutomationCommands.showTrackAutomation, { trackId: track.id, show: !track.showAutomation }),
            );
          }}
          title="Show automation lanes"
          aria-label={`Automation for ${track.name}`}
          aria-pressed={track.showAutomation}
        >
          A
        </button>
        {(track.kind === 'audio' || track.kind === 'instrument') && (
          <button
            className={`chip chip--arm ${track.armed ? 'chip--on' : ''}`}
            onClick={() => {
              dispatch(command(TrackCommands.setTrackArm, { trackId: track.id, armed: !track.armed }));
              if (!track.armed) void useDaw.getState().refreshInputDevices();
            }}
            title="Arm for recording"
            aria-pressed={track.armed}
          >
            ●
          </button>
        )}
      </div>

      {!compact && (
        <>
          <div className="track-head__row">
            <input
              className="slider"
              type="range"
              min={0}
              max={1.6}
              step={0.001}
              value={track.volume}
              aria-label={`${track.name} volume`}
              onPointerDown={() => useDaw.getState().touchParameter(volumeTarget)}
              onChange={(e) => {
                const volume = Number(e.target.value);
                dispatch(command(TrackCommands.setTrackVolume, { trackId: track.id, volume }));
                useDaw.getState().writeParameter(volumeTarget, volume / 4);
              }}
              onPointerUp={() => {
                useDaw.getState().releaseParameter(volumeTarget);
                getServices().commands.breakCoalescing();
              }}
            />
            <span className="channel__value" style={{ width: 42 }}>
              {formatDb(track.volume)}
            </span>
            <div style={{ height: 22, display: 'flex' }}>
              <Meter trackId={track.id} label={track.name} />
            </div>
          </div>

          <div className="track-head__row">
            <span className="track-head__kind">{KIND_LABEL[track.kind]}</span>
            <input
              className="slider"
              type="range"
              min={-1}
              max={1}
              step={0.01}
              value={track.pan}
              aria-label={`${track.name} pan`}
              onPointerDown={() => useDaw.getState().touchParameter(panTarget)}
              onChange={(e) => {
                const pan = Number(e.target.value);
                dispatch(command(TrackCommands.setTrackPan, { trackId: track.id, pan }));
                useDaw.getState().writeParameter(panTarget, (pan + 1) / 2);
              }}
              onPointerUp={() => {
                useDaw.getState().releaseParameter(panTarget);
                getServices().commands.breakCoalescing();
              }}
            />
            <span className="channel__value" style={{ width: 30 }}>
              {formatPan(track.pan)}
            </span>
          </div>
        </>
      )}

      {track.armed && track.kind === 'instrument' && height >= 110 && <MidiInputRow />}

      {track.armed && track.kind === 'audio' && height >= 110 && (
        <div className="track-head__row">
          <button
            className={`chip chip--mon ${track.inputMonitor ? 'chip--on' : ''}`}
            onClick={() => dispatch(command(TrackCommands.setTrackMonitor, { trackId: track.id, monitor: !track.inputMonitor }))}
            title="Monitor the live input through this track"
          >
            MON
          </button>
          <select
            className="select"
            style={{ flex: 1, minWidth: 0, height: 20 }}
            value={track.inputDeviceId ?? ''}
            aria-label="Recording input"
            onChange={(e) =>
              dispatch(command(TrackCommands.setTrackInputDevice, { trackId: track.id, deviceId: e.target.value || null }))
            }
          >
            <option value="">Default input</option>
            {inputDevices.map((device) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div
        className="track-head__resize"
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={onResizePointerUp}
        role="separator"
        aria-label={`Resize ${track.name}`}
      />
    </div>
  );
}

/**
 * Vertical zoom for the whole track column. It scales every lane at once and
 * leaves the per-track heights alone, so the relative sizes a user has set by
 * dragging survive. View state, so it is not undoable: sizing is not editing.
 */
function TrackHeightSlider(): JSX.Element {
  const scale = useDaw((s) => trackHeightScale(s.project));
  return (
    <label className="track-headers__zoom" title={`Track height (${Math.round(scale * 100)}%)`}>
      <span className="track-headers__zoom-label" aria-hidden="true">
        H
      </span>
      <input
        className="slider"
        type="range"
        min={MIN_TRACK_HEIGHT_SCALE}
        max={MAX_TRACK_HEIGHT_SCALE}
        step={0.05}
        value={scale}
        aria-label="Track height"
        aria-valuetext={`${Math.round(scale * 100)} percent`}
        onChange={(e) => useDaw.getState().setView({ trackHeightScale: Number(e.target.value) })}
        onDoubleClick={() => useDaw.getState().setView({ trackHeightScale: 1 })}
      />
    </label>
  );
}

export function formatDb(gain: number): string {
  const db = gainToDb(gain);
  if (!Number.isFinite(db)) return '-inf';
  return `${db > 0 ? '+' : ''}${db.toFixed(1)}`;
}

export function formatPan(pan: number): string {
  if (Math.abs(pan) < 0.005) return 'C';
  return `${pan < 0 ? 'L' : 'R'}${Math.round(Math.abs(pan) * 100)}`;
}

/** Parameters on a track that can be automated and do not already have a lane. */
function availableTargets(
  project: ReturnType<typeof useDaw.getState>['project'],
  track: Track,
): { key: string; label: string; kind: 'track' | 'effect'; effectId: string | null; paramId: string }[] {
  const taken = new Set(
    track.automationLaneIds
      .map((id) => project.automation[id])
      .filter(Boolean)
      .map((lane) => `${lane.target.effectId ?? 'track'}:${lane.target.paramId}`),
  );

  const options: ReturnType<typeof availableTargets> = [];
  for (const param of TRACK_PARAM_DESCRIPTORS) {
    if (taken.has(`track:${param.id}`)) continue;
    options.push({ key: `track:${param.id}`, label: param.label, kind: 'track', effectId: null, paramId: param.id });
  }
  for (const effect of track.effects) {
    const descriptor = effectDescriptor(effect);
    for (const param of descriptor.params) {
      if (!isAutomatable(param)) continue;
      if (taken.has(`${effect.id}:${param.id}`)) continue;
      options.push({
        key: `${effect.id}:${param.id}`,
        label: `${descriptor.label} · ${param.label}`,
        kind: 'effect',
        effectId: effect.id,
        paramId: param.id,
      });
    }
  }
  return options;
}

/**
 * The header for one automation lane. It carries only what has to be reachable
 * while drawing a curve: which parameter this is, whether it is active, and a
 * way to remove it.
 */
function AutomationLaneHeader({
  lane,
  track,
  height,
  selected,
}: {
  lane: AutomationLane;
  track: Track;
  height: number;
  selected: boolean;
}): JSX.Element {
  const project = useDaw((s) => s.project);
  const dispatch = useDaw((s) => s.dispatch);
  const options = availableTargets(project, track);
  const isLast = track.automationLaneIds[track.automationLaneIds.length - 1] === lane.id;

  return (
    <div
      className="track-head automation-head"
      style={{ height, background: selected ? 'var(--bg-2)' : undefined }}
      onPointerDown={() => useDaw.getState().setSelection({ trackId: track.id, laneId: lane.id })}
    >
      <div className="track-head__accent" style={{ background: track.color, opacity: 0.45 }} />

      <div className="track-head__row">
        <span className="automation-head__name" title={describeTarget(project, lane.target)}>
          {describeTarget(project, lane.target)}
        </span>
        <button
          className={`chip ${lane.enabled ? 'chip--mon chip--on' : ''}`}
          onClick={() =>
            dispatch(command(AutomationCommands.setLaneEnabled, { laneId: lane.id, enabled: !lane.enabled }))
          }
          title={lane.enabled ? 'Automation is active' : 'Automation is bypassed'}
          aria-pressed={lane.enabled}
        >
          ON
        </button>
        <button
          className="chip"
          onClick={() => dispatch(command(AutomationCommands.removeAutomationLane, { laneId: lane.id }))}
          title="Remove this automation lane"
          aria-label={`Remove ${describeTarget(project, lane.target)} automation`}
        >
          ✕
        </button>
      </div>

      {isLast && options.length > 0 && height >= 52 && (
        <select
          className="select"
          style={{ height: 18, fontSize: 10 }}
          value=""
          aria-label={`Add an automation lane to ${track.name}`}
          onChange={(e) => {
            const option = options.find((o) => o.key === e.target.value);
            if (!option) return;
            dispatch({
              type: AutomationCommands.addAutomationLane.type,
              params: {
                trackId: track.id,
                kind: option.kind,
                effectId: option.effectId,
                paramId: option.paramId,
              },
            });
            e.target.value = '';
          }}
        >
          <option value="">+ Automation…</option>
          {options.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/**
 * MIDI input controls, shown on an armed instrument track.
 *
 * Web MIDI is requested from this button rather than on load, so the permission
 * prompt always has a visible cause. When there is no controller — or no Web
 * MIDI at all — the row says what still works instead of showing an empty list.
 */
function MidiInputRow(): JSX.Element {
  const status = useDaw((s) => s.midiStatus);
  const devices = useDaw((s) => s.midiDevices);
  const deviceId = useDaw((s) => s.midiDeviceId);
  const octave = useDaw((s) => s.keyboardOctave);

  if (status !== 'open') {
    return (
      <div className="track-head__row">
        <button
          className="chip"
          disabled={status !== 'available'}
          onClick={() => void useDaw.getState().enableMidi()}
          title={
            status === 'available'
              ? 'Connect a MIDI controller'
              : status === 'insecure'
                ? 'Web MIDI needs a secure (https) page'
                : 'This browser does not provide Web MIDI'
          }
        >
          MIDI
        </button>
        <span className="track-head__kind" style={{ flex: 1 }}>
          {status === 'available' ? 'Connect a controller' : 'Type to play · Z/X octave'}
        </span>
      </div>
    );
  }

  return (
    <div className="track-head__row">
      <select
        className="select"
        style={{ flex: 1, minWidth: 0, height: 20 }}
        value={deviceId ?? ''}
        aria-label="MIDI input device"
        onChange={(e) => useDaw.getState().selectMidiDevice(e.target.value || null)}
      >
        <option value="">All MIDI inputs</option>
        {devices.map((device) => (
          <option key={device.id} value={device.id}>
            {device.name}
          </option>
        ))}
      </select>
      <span className="track-head__kind" title="Octave the computer keyboard plays in">
        C{octave}
      </span>
    </div>
  );
}

/**
 * The header for one take lane.
 *
 * It says which take this is and offers the two things worth reaching for while
 * comping: use the whole take, or throw it away. Everything finer is done by
 * dragging across the lane itself.
 */
function TakeLaneHeader({
  track,
  takeLane,
  height,
}: {
  track: Track;
  takeLane: number;
  height: number;
}): JSX.Element {
  const project = useDaw((s) => s.project);
  const dispatch = useDaw((s) => s.dispatch);

  const clips = clipsOnLane(project, track.id, takeLane);
  const name = trackTakeName(track, takeLane);
  const span = clips.reduce(
    (acc, clip) => ({
      from: Math.min(acc.from, clip.startTicks),
      to: Math.max(acc.to, Math.round(clipEndTicks(project, clip))),
    }),
    { from: Number.MAX_SAFE_INTEGER, to: 0 },
  );

  return (
    <div className="track-head take-head" style={{ height }}>
      <div className="track-head__accent" style={{ background: track.color, opacity: 0.3 }} />
      <div className="track-head__row">
        <input
          className="take-head__name"
          value={name}
          spellCheck={false}
          maxLength={MAX_TAKE_NAME}
          onChange={(e) =>
            dispatch(command(TakeCommands.renameTake, { trackId: track.id, takeLane, name: e.target.value }))
          }
          title={`${name} — rename it to whatever the pass was`}
          aria-label={`Name of ${name}`}
        />
        <button
          className="chip"
          disabled={clips.length === 0}
          onClick={() =>
            dispatch(
              command(TakeCommands.promoteTake, {
                trackId: track.id,
                takeLane,
                startTicks: span.from,
                endTicks: span.to,
              }),
            )
          }
          title="Use this take for its whole length"
          aria-label={`Use ${name}`}
        >
          USE
        </button>
        <button
          className="chip"
          onClick={() => dispatch(command(TakeCommands.removeTakeLane, { trackId: track.id, takeLane }))}
          title="Delete this take"
          aria-label={`Delete ${name}`}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
